import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { SettingsConfirmModalService } from './settings-confirm-modal.service';
import { SettingsConfirmModalOptions } from './settings-confirm-modal.options';

/**
 * Generic confirm/delete modal (LUNA-28). Deliberately not a variant of `ConfirmationModal`
 * (the "type CONFIRM to proceed" flow this ticket retires) or `ConfirmationMessage`/
 * `ConfirmationBoxService` (a bare Yes/No link box with no title, icon, or button styling) —
 * neither matches the "Simple settings UX/UI" Figma spec. Fixed title/message/button-pair shape
 * (no content projection), so its own host is the full backdrop + centered card, unlike
 * `SettingsSidePanel` which has no backdrop at all.
 */
@Component({
  selector: 'cs-settings-confirm-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './settings-confirm-modal.html',
  styleUrl: './settings-confirm-modal.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  host: {
    class: 'cs-settings-confirm-modal-host',
    role: 'dialog',
    tabindex: '-1',
    '[attr.aria-label]': 'config.title || null',
  },
})
export class SettingsConfirmModal implements OnInit {
  config: SettingsConfirmModalOptions;
  isShown = false;

  private closeTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    options: SettingsConfirmModalOptions,
    private modalService: SettingsConfirmModalService,
    private _element: ElementRef,
    private _renderer: Renderer2,
  ) {
    this.config = options;
  }

  ngOnInit(): void {
    this._renderer.setStyle(this._element.nativeElement, 'display', 'flex');
    // Deferred so the click that opened this modal doesn't register as a host/backdrop click.
    setTimeout(() => {
      this.isShown = true;
      this._renderer.addClass(this._element.nativeElement, 'show');
    }, 20);
  }

  confirm(): void {
    this.config.onConfirm?.();
    this.close();
  }

  cancel(): void {
    this.config.onCancel?.();
    this.close();
  }

  close(): void {
    if (this.closeTimeout) {
      return;
    }
    this._renderer.removeClass(this._element.nativeElement, 'show');
    // Keep in sync with $settings-confirm-modal-transition-duration in settings-confirm-modal.scss.
    this.closeTimeout = setTimeout(() => {
      this.isShown = false;
      this.modalService.remove(this.config.id);
    }, 250);
  }
}
